import React, { useMemo } from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { PRICE_RANGE } from '../../../constants';

interface PriceDistributionChartProps {
  prices: number[];
  numBins?: number;
  height?: number;
  barGap?: number;
}

interface PriceBin {
  startPrice: number;
  endPrice: number;
  count: number;
}

const DEFAULT_HEIGHT = 40; // Fixed height in pixels
const DEFAULT_NUM_BINS = 20;
const DEFAULT_BAR_GAP = 1;

export const PriceDistributionChart: React.FC<PriceDistributionChartProps> = ({ prices, numBins = DEFAULT_NUM_BINS, height = DEFAULT_HEIGHT, barGap = DEFAULT_BAR_GAP }) => {
  const bins = useMemo(() => {
    if (!prices.length) return [];

    // Use fixed min and max prices from constants
    const minPrice = PRICE_RANGE.MIN;
    const maxPrice = PRICE_RANGE.MAX;
    const binWidth = (maxPrice - minPrice) / numBins;

    // Initialize bins
    const priceBins: PriceBin[] = Array.from({ length: numBins }, (_, i) => ({
      startPrice: minPrice + i * binWidth,
      endPrice: minPrice + (i + 1) * binWidth,
      count: 0,
    }));

    // Count prices in each bin
    prices.forEach(price => {
      const binIndex = Math.min(Math.floor(((price - minPrice) / (maxPrice - minPrice)) * numBins), numBins - 1);
      priceBins[binIndex].count++;
    });

    return priceBins;
  }, [prices, numBins]);

  const maxCount = useMemo(() => Math.max(...bins.map(bin => bin.count), 1), [bins]);

  return (
    <Box
      width="100%"
      height={pxToRem(height)}
      position="relative"
    >
      <Flex
        width="100%"
        height="100%"
        alignItems="flex-end"
        justifyContent="space-between"
        gap={pxToRem(barGap)}
      >
        {bins.map((bin, index) => (
          <Box
            key={index}
            height={`${(bin.count / maxCount) * 100}%`}
            flex={1}
            backgroundColor="accentBlue2"
            opacity={0.6}
            borderTopLeftRadius={pxToRem(3)}
            borderTopRightRadius={pxToRem(3)}
          />
        ))}
      </Flex>
    </Box>
  );
};
